import { ReactNode, useMemo, useState } from 'react';
import { Box, IconButton } from '@material-ui/core';
import ArrowBackRoundedIcon from '@material-ui/icons/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@material-ui/icons/ArrowForwardRounded';
import ZoomInRoundedIcon from '@material-ui/icons/ZoomInRounded';
import ZoomOutRoundedIcon from '@material-ui/icons/ZoomOutRounded';

import { SBase, SText } from '@setvi/shared/components';
import { Document } from '@setvi/shared/components/sresource-children-list/components/page-preview';
import { Resource } from '@setvi/shared/interfaces';

import type { PreviewPage } from './types';
import { useStyles } from './styles';

export type { PreviewPage } from './types';

export const PREVIEW_ZOOM_DIALOG_ID = 'print-order-preview-zoom';

// Rendered page width at 100%; zoom scales it, the scroll area handles the
// overflow.
const BASE_PAGE_WIDTH = 940;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

interface PreviewZoomDialogProps {
  title: string;
  /** Fallback document when a page carries no generated pdf url. */
  resource?: Resource;
  /**
   * Entries to page through. An entry with `pdfPage` shows that fixed page;
   * an entry without it spans its whole PDF — the pager walks through all of
   * its pages (count discovered once the document loads).
   */
  pages: PreviewPage[];
  initialIndex?: number;
  /** Right-aligned toolbar content, e.g. an Add to Cart button. */
  actions?: ReactNode;
  onClose: () => void;
}

export const PreviewZoomDialog = ({
  title,
  resource,
  pages,
  initialIndex = 0,
  actions,
  onClose
}: PreviewZoomDialogProps) => {
  const classes = useStyles();
  const [zoom, setZoom] = useState(1);
  const [index, setIndex] = useState(
    Math.min(Math.max(initialIndex, 0), Math.max(0, pages.length - 1))
  );
  // Page within the active whole-document entry (one without `pdfPage`).
  const [innerPage, setInnerPage] = useState(1);
  // Discovered page counts, per document url.
  const [pageCounts, setPageCounts] = useState<Record<string, number>>({});

  const activeEntry = pages[index];
  const isWholeDoc = !!activeEntry && activeEntry.pdfPage == null;
  const activeUrl = activeEntry?.pdfUrl ?? resource?.DownloadURLWithSas;
  const docPages = activeUrl ? pageCounts[activeUrl] : undefined;

  const previewDocument = useMemo(
    () =>
      ({
        ...(resource ?? {}),
        DownloadURLWithSas: activeUrl
      }) as Resource,
    [resource, activeUrl]
  );

  const goPrev = () => {
    if (isWholeDoc && innerPage > 1) {
      setInnerPage(page => page - 1);
      return;
    }
    // Stepping back into a whole-document entry lands on its LAST page (when
    // its count is already known), mirroring forward paging.
    const prevIndex = Math.max(0, index - 1);
    const prevEntry = pages[prevIndex];
    const prevUrl = prevEntry?.pdfUrl ?? resource?.DownloadURLWithSas;
    const prevIsWholeDoc = !!prevEntry && prevEntry.pdfPage == null;
    setIndex(prevIndex);
    setInnerPage(prevIsWholeDoc ? (prevUrl && pageCounts[prevUrl]) || 1 : 1);
  };

  const goNext = () => {
    if (isWholeDoc) {
      // The document's page count isn't known until it loads — stay put
      // rather than skipping its remaining pages.
      if (!docPages) return;
      if (innerPage < docPages) {
        setInnerPage(page => page + 1);
        return;
      }
    }
    setIndex(i => Math.min(pages.length - 1, i + 1));
    setInnerPage(1);
  };

  const prevDisabled = index === 0 && (!isWholeDoc || innerPage === 1);
  const nextDisabled = isWholeDoc
    ? !docPages || (index === pages.length - 1 && innerPage >= docPages)
    : index === pages.length - 1;
  const showPager = pages.length > 1 || (isWholeDoc && (docPages ?? 1) > 1);

  const pagerLabel = isWholeDoc
    ? `${activeEntry?.label} · page ${innerPage} of ${docPages ?? 1}`
    : `${activeEntry?.label} · page ${index + 1} of ${pages.length}`;

  return (
    <SBase width="95vw" height="95vh" header={{ title, onClose }}>
      <Box className={classes.toolbar}>
        <Box />
        <Box className={classes.toolbarControls}>
          <IconButton
            className={classes.toolbarButton}
            size="small"
            title="Zoom out"
            disabled={zoom <= MIN_ZOOM}
            onClick={() => setZoom(z => Math.max(MIN_ZOOM, z - ZOOM_STEP))}>
            <ZoomOutRoundedIcon fontSize="small" />
          </IconButton>
          <SText
            title={`${Math.round(zoom * 100)}%`}
            size="sm"
            fontColor="#5D7083"
            className={classes.zoomLabel}
          />
          <IconButton
            className={classes.toolbarButton}
            size="small"
            title="Zoom in"
            disabled={zoom >= MAX_ZOOM}
            onClick={() => setZoom(z => Math.min(MAX_ZOOM, z + ZOOM_STEP))}>
            <ZoomInRoundedIcon fontSize="small" />
          </IconButton>

          {showPager && (
            <>
              <Box className={classes.divider} />
              <IconButton
                className={classes.toolbarButton}
                size="small"
                disabled={prevDisabled}
                onClick={goPrev}>
                <ArrowBackRoundedIcon fontSize="small" />
              </IconButton>
              <SText title={pagerLabel} size="sm" fontColor="#5D7083" />
              <IconButton
                className={classes.toolbarButton}
                size="small"
                disabled={nextDisabled}
                onClick={goNext}>
                <ArrowForwardRoundedIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
        <Box className={classes.toolbarActions}>{actions}</Box>
      </Box>

      <Box className={classes.scrollArea}>
        <Box className={classes.doc}>
          <Document
            document={previewDocument}
            loading={false}
            activePage={isWholeDoc ? innerPage : (activeEntry?.pdfPage ?? 1)}
            pageWidth={Math.round(BASE_PAGE_WIDTH * zoom)}
            onLoadPages={numPages => {
              if (!activeUrl) return;
              setPageCounts(prev =>
                prev[activeUrl] === numPages
                  ? prev
                  : { ...prev, [activeUrl]: numPages }
              );
            }}
          />
        </Box>
      </Box>
    </SBase>
  );
};
